import { useState, useEffect } from 'react'
import { useFetchData } from '../hooks/useFetchData'
import { 
  Newspaper, Calendar, Tag, ExternalLink, Filter,
  TrendingUp, Shield, Plane, Ship, Target, AlertCircle,
  Clock, BookOpen, Award, Zap
} from 'lucide-react'

export default function DefenceNews() {
  const { data, loading, error } = useFetchData('/data/defence_news.json')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedForce, setSelectedForce] = useState('all')
  const [sortBy, setSortBy] = useState('date') // date, importance

  const news = data?.news || []
  const categories = data?.categories || []
  const forces = data?.forces || []

  // Filter and sort news
  const filteredNews = news
    .filter(item => {
      const categoryMatch = selectedCategory === 'all' || item.category === selectedCategory
      const forceMatch = selectedForce === 'all' || item.force === selectedForce
      return categoryMatch && forceMatch
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.date) - new Date(a.date)
      }
      // Sort by importance
      const importanceOrder = { high: 3, medium: 2, low: 1 }
      return (importanceOrder[b.importance] || 0) - (importanceOrder[a.importance] || 0)
    })

  const getForceIcon = (force) => {
    switch (force) {
      case 'army': return <Shield className="w-4 h-4" />
      case 'navy': return <Ship className="w-4 h-4" />
      case 'air-force': return <Plane className="w-4 h-4" />
      case 'strategic-forces': return <Target className="w-4 h-4" />
      default: return <Shield className="w-4 h-4" />
    }
  }

  const getImportanceBadge = (importance) => {
    const badges = {
      high: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
      medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
      low: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
    }
    return badges[importance] || badges.medium
  }

  const isNewNews = (dateStr) => {
    const newsDate = new Date(dateStr)
    const today = new Date()
    const diffTime = Math.abs(today - newsDate)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays <= 7 // News from last 7 days
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2">Loading defence news...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-800 dark:text-red-200">Error Loading News</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Newspaper className="w-8 h-8 text-primary-600" />
            Defence News & Updates
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Stay updated with the latest defence news and developments
          </p>
        </div>

        {/* Stats Banner */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-blue-600" />
              <div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {news.length}
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-300">Total News Articles</div>
              </div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-green-600" />
              <div>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {news.filter(n => isNewNews(n.date)).length}
                </div>
                <div className="text-sm text-green-700 dark:text-green-300">This Week</div>
              </div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-700">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-orange-600" />
              <div>
                <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                  {news.filter(n => n.importance === 'high').length}
                </div>
                <div className="text-sm text-orange-700 dark:text-orange-300">High Priority</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <span className="font-medium">Filter News</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.split('-').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Force
              </label>
              <select
                value={selectedForce}
                onChange={(e) => setSelectedForce(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
              >
                <option value="all">All Forces</option>
                {forces.map(force => (
                  <option key={force} value={force}>
                    {force.split('-').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800"
              >
                <option value="date">Latest First</option>
                <option value="importance">Most Important</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* News Articles */}
      <div className="space-y-4">
        {filteredNews.length === 0 ? (
          <div className="card text-center py-12">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">
              No news articles found for the selected filters.
            </p>
          </div>
        ) : (
          filteredNews.map(item => (
            <div 
              key={item.id} 
              className="card hover:shadow-lg transition-shadow duration-300 border-l-4"
              style={{
                borderLeftColor: item.importance === 'high' ? '#ef4444' : 
                                item.importance === 'medium' ? '#f59e0b' : '#3b82f6'
              }}
            >
              <div className="flex flex-col md:flex-row gap-4">
                {/* Image */}
                {item.imageUrl && (
                  <div className="md:w-48 flex-shrink-0">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-48 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  </div>
                )}

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex-1">
                      {item.title}
                      {isNewNews(item.date) && (
                        <span className="ml-2 px-2 py-1 text-xs font-bold bg-gradient-to-r from-orange-400 to-pink-500 text-white rounded-full animate-pulse">
                          NEW
                        </span>
                      )}
                    </h2>
                  </div>

                  {/* Meta Information */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                      <Calendar className="w-4 h-4" />
                      {new Date(item.date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>

                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getImportanceBadge(item.importance)}`}>
                      {getForceIcon(item.force)}
                      {item.force.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </span>

                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                      {item.category.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </span>
                  </div>

                  {/* Summary */}
                  <p className="text-gray-700 dark:text-gray-300 mb-3 font-medium">
                    {item.summary}
                  </p>

                  {/* Full Content */}
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 leading-relaxed">
                    {item.content}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.tags.map(tag => (
                      <span 
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"
                      >
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    {item.relatedQuestions && item.relatedQuestions.length > 0 && (
                      <button className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        Practice Questions ({item.relatedQuestions.length})
                      </button>
                    )}

                    {item.readMoreUrl && (
                      <a
                        href={item.readMoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Read Full Article
                      </a>
                    )}

                    <div className="ml-auto text-xs text-gray-500 dark:text-gray-500 flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {item.source}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer Note */}
      <div className="card bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500">
        <div className="flex items-start gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 dark:text-blue-100">
            <strong>Stay Updated:</strong> Check this page daily for the latest defence news and updates. 
            All news articles are linked to practice questions to help you prepare better for SSB interviews.
          </div>
        </div>
      </div>
    </div>
  )
}
